import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, X, ArrowRight, Paperclip, Upload, CircleCheck,
  ChartNoAxesCombined, WalletCards, Trophy, Copy, ChevronRight, ArrowUp, ArrowDown, ChevronDown, RefreshCw,
} from 'lucide-react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import Pagination from '../../../components/Pagination/Pagination';
import PageFooter from '../../../components/PageFooter/PageFooter';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Button from '../../../components/Button/Button';
import StatusTooltip from '../../../components/StatusTooltip/StatusTooltip';
import SlackText from '../../../components/SlackText/SlackText';
import RichTextEditor from '../../../components/RichText/RichTextEditor';
import RichTextView from '../../../components/RichText/RichTextView';
import { isBlankDocument, textLength, plainText } from '../../../components/RichText/richText';
import { videoEmbedUrl, isVideoUrl } from '../../../utils/videoLinks';
import api from '../../../services/api';
import styles from './HelpCenter.module.css';

const ISSUE_CATEGORIES = ['Assets', 'Wallet', 'Leaderboard', 'Trade Copier', 'Team', 'Technical issue', 'Login issue', 'Account Merge', 'Update Email', 'Other'];

const FAQ_CATEGORIES = [
  { id: 'assets',       Icon: ChartNoAxesCombined, label: 'My assets',    count: 11, desc: 'Performance, reports, and account analysis.' },
  { id: 'wallet',       Icon: WalletCards,          label: 'Wallet',       count: 8,  desc: 'Everything related to withdrawals, balances, payouts and transfers.' },
  { id: 'leaderboard',  Icon: Trophy,               label: 'Leaderboard',  count: 6,  desc: 'Rankings, scoring, and trader comparisons.' },
  { id: 'trade-copier', Icon: Copy,                 label: 'Trade Copier', count: 10, desc: 'How to use and manage your trade copier connections.' },
];

const POPULAR_ARTICLES = [
  { title: 'How do payouts work?',                                catId: 'wallet' },
  { title: 'Why is my account showing a loss?',                   catId: 'assets' },
  { title: 'What happens if my account reaches maximum drawdown?', catId: 'assets' },
  { title: 'How do I access my MT5 account?',                     catId: 'assets' },
  { title: 'How long do payouts take?',                           catId: 'wallet' },
  { title: 'How do I request a payout?',                          catId: 'wallet' },
  { title: 'How are leaderboard rankings calculated?',            catId: 'leaderboard' },
  { title: 'What is the Trade Copier?',                           catId: 'trade-copier' },
];

const CATEGORY_ARTICLES = {
  assets: [
    'What does Balance, Equity, and Profit mean?', 'How do I access my MT5 account?', 'Why is my account showing a loss?',
    'What happens if my account reaches maximum drawdown?', 'How often is account data updated?', 'Can I have multiple POW accounts?',
    'How do funded accounts work?', 'What happens when my account is liquidated?', 'Why is my account inactive?',
    'How do I change my MT5 password?', 'Why is my account temporarily disabled?',
  ],
  wallet: [
    'How do payouts work?', 'How do I request a payout?', 'What is available to withdraw?',
    'What is Cumulative Profit?', 'What is Net Withdrawals?', 'Why was my payout denied?',
    'How long do payouts take?', 'Are there any payout fees?',
  ],
  leaderboard: [
    'How are leaderboard rankings calculated?', 'What is Assets Under Management (AUM)?', 'What is Return on Fees?',
    'Why did my rank change?', 'How often does the leaderboard update?', 'Can I hide my profile from the leaderboard?',
  ],
  'trade-copier': [
    'What is the Trade Copier?', 'How do I connect a slave account?', 'How do I add a master account?',
    'How does the copy multiplier work?', 'How do I switch masters?', 'What happens if my master account disconnects?',
    'Why is my copier showing an error?', 'How is risk managed when copying trades?', 'Can I copy multiple masters at once?',
    'How do I disconnect a copied account?',
  ],
};

const ARTICLE_STEPS = {
  // ── My Assets ──
  'What does Balance, Equity, and Profit mean?': [
    { title: 'OVERVIEW', body: 'These three figures appear on your account dashboard and each measures something different about the current state of your account.' },
    { title: 'BALANCE', body: 'Total value of your account after all closed trades have been settled. It only changes when a position is closed or a payout is processed.' },
    { title: 'EQUITY', body: 'Your balance plus or minus the floating profit or loss of any open positions. If you have no open trades, equity equals balance. Equity fluctuates in real time as the market moves.' },
    { title: 'PROFIT', body: 'Net gain or loss across your open positions at the current moment. This is an unrealized figure - it is not locked in until those positions are closed.' },
  ],
  'How do I access my MT5 account?': [
    { title: 'OVERVIEW', body: 'Your DFY funded account runs on MetaTrader 5 (MT5). You can access it using any MT5-compatible desktop, web, or mobile device.' },
    { title: 'YOUR ACCOUNT CREDENTIALS', body: 'Once your DFY account is activated, your MT5 credentials (account number, password, and broker server) are available in the My Assets section of your portal.' },
    { title: 'LOGGING IN', body: 'Go to My Assets in the portal and click MT5 Login below your listed trading accounts to download and install MetaTrader 5.\nOpen MT5 and navigate to File > Open an Account. A new window will appear.\nIn the search bar, type TCBridge and press Enter.\nSelect the TCBridge entry from the list and click Next.\nSelect Connect with an existing trade account, then enter your Login (account number) and Password. Open the Server dropdown and select TCBridge-Live.\nClick OK. Your account balance, positions, and history will load once connected.' },
    { title: 'TROUBLE CONNECTING?', body: "Double-check the server name matches exactly what appears in your portal.\nEnsure your internet connection is stable.\nTry the MT5 mobile terminal as an alternative if the desktop client has issues.\nIf credentials still don't work, open a help ticket and the team will investigate." },
  ],
  'Why is my account showing a loss?': [
    { title: 'OVERVIEW', body: 'Seeing a negative figure on your account can be alarming, but there are a few straightforward reasons this can happen.' },
    { title: 'FLOATING LOSSES ON OPEN POSITIONS', body: 'The most common reason is unrealized losses from open trades. These reduce your equity in real time as the market moves against your positions. Once a trade is closed, the loss becomes realized and your balance adjusts accordingly.' },
    { title: 'SWAP CHARGES', body: 'Holding positions overnight incurs swap (rollover) charges. These are debited directly from your account and reduce your balance over time, which can cause a net loss if trading activity has been limited.' },
    { title: 'CLOSED LOSING TRADES', body: 'If recent trades were closed at a loss, your balance will reflect this. Check your trade history in MT5 under the History tab to review closed positions.' },
    { title: 'WHAT TO DO', body: 'Review your open positions and trade history in MT5 to identify the source of the loss. If figures appear incorrect or you suspect a technical error, contact support with your account ID.' },
  ],
  'What happens if my account reaches maximum drawdown?': [
    { title: 'OVERVIEW', body: 'Your DFY account is subject to a 7.2% maximum drawdown limit calculated from the starting account balance. This is a hard risk control enforced to protect the integrity of the program.' },
    { title: 'WHAT TRIGGERS THE DRAWDOWN LIMIT?', body: 'Drawdown is measured as the decline in your account equity from the starting balance. If your equity falls to or below 92.8% of the starting balance, the threshold is reached.' },
    { title: 'WHAT HAPPENS AT THAT POINT?', body: 'Open positions will be liquidated and your account will be closed automatically.\nImportant: Liquidation may be triggered slightly before the full 7.2% drawdown level is reached, based on market conditions, execution risk, or liquidity constraints at the time.' },
    { title: 'CAN I REACTIVATE MY ACCOUNT?', body: 'Once an account is closed due to drawdown, it cannot be reopened. You would need to pay a new Activation Payment to access a new DFY account.' },
  ],
  'How often is account data updated?': [
    { title: 'OVERVIEW', body: 'Your MT5 terminal shows live data in real time - balance, equity, open positions, and floating P&L all update tick by tick while the terminal is connected.' },
    { title: 'PORTAL SYNCING', body: 'The portal dashboard syncs account metrics every 5 seconds. Minor delays of up to a minute are normal. For the most accurate real-time view of your account, use MT5 directly.' },
    { title: 'DATA IS NOT UPDATING', body: 'If your account data has not refreshed for an extended period, try refreshing the page first. If the issue persists, raise a support ticket and the team will investigate.' },
  ],
  'Can I have multiple POW accounts?': [
    { title: 'OVERVIEW', body: 'Yes. You can hold more than one DFY funded account under your profile, subject to program availability.' },
    { title: 'HOW MULTIPLE ACCOUNTS WORK', body: 'Each account operates independently with its own starting balance, drawdown limit, cumulative profit, and payout history. Additional accounts require a separate Activation Payment. Contact support for available allocation sizes.' },
  ],
  'How do funded accounts work?': [
    { title: 'OVERVIEW', body: 'A POW DFY funded account gives you access to a trading allocation without risking your own capital. Here is how the model works.' },
    { title: 'ACTIVATION', body: 'You pay an Activation Payment to access the DFY program. This grants you a funded MT5 trading account with a set allocation size. The Activation Payment is not a deposit - it does not appear as your account balance.' },
    { title: 'TRADING', body: 'All profits and losses are tracked against the starting balance. The account is subject to a 7.2% maximum drawdown limit.' },
    { title: 'PAYOUTS', body: 'Profit share withdrawal requests are open from the 20th to the 29th of each month at 23:59, via the Wallet section.' },
  ],
  'What happens when my account is liquidated?': [
    { title: 'OVERVIEW', body: 'Liquidation occurs when your account equity reaches or falls below the 7.2% maximum drawdown threshold from the starting balance.' },
    { title: 'WHAT HAPPENS DURING LIQUIDATION?', body: 'All open positions are closed automatically by the risk system to stop further losses and protect the integrity of the funded program.' },
    { title: 'CAN LIQUIDATION HAPPEN BEFORE 7.2%?', body: 'Yes. Liquidation may trigger before the full 7.2% level is reached, based on market conditions, execution risk, or liquidity constraints at the time.' },
    { title: 'WHAT HAPPENS TO MY ACCOUNT AFTER LIQUIDATION?', body: 'Your account is closed and cannot be reopened. A new Activation Payment would be required to access a new funded account.' },
  ],
  'Why is my account inactive?': [
    { title: 'OVERVIEW', body: 'An inactive account has either been suspended by POW or closed following a liquidation event.' },
    { title: 'ACCOUNT SUSPENDED', body: 'POW may suspend or terminate access to an account at its discretion for risk, compliance, or integrity reasons. Contact support with your account ID for details.' },
    { title: 'ACCOUNT LIQUIDATED', body: 'If your account reached the 7.2% maximum drawdown threshold, positions will have been closed automatically and the account shut down.' },
    { title: 'WHAT TO DO', body: 'Contact support with your account ID. The team will confirm whether the account was suspended or liquidated and advise on next steps.' },
  ],
  'How do I change my MT5 password?': [
    { title: 'OVERVIEW', body: 'Open a help ticket with your account ID and request a password change. Once processed, your updated password will be available under My Assets > MT5 Login, below the relevant account.' },
  ],
  'Why is my account temporarily disabled?': [
    { title: 'OVERVIEW', body: 'POW may suspend or restrict access to your account at its discretion for risk, compliance, or integrity reasons. The specific reason will not always be disclosed upfront.' },
    { title: 'WHAT TO DO', body: 'Open a help ticket with your account ID. The team will confirm the nature of the restriction and advise on next steps and expected resolution timeline.' },
  ],

  // ── Wallet ──
  'How do payouts work?': [
    { title: 'OVERVIEW', body: 'Payouts are how you receive your share of profits earned through your DFY funded account.' },
    { title: 'HOW IT WORKS', body: 'Once you have a positive Available to Withdraw balance, you can submit a payout request through the Wallet section of your portal. Your payout represents your share of the profits generated - it is not a withdrawal of a deposited balance.' },
  ],
  'How do I request a payout?': [
    { title: 'OVERVIEW', body: 'Payout requests are submitted through the Wallet section. The withdrawal window runs from the 21st to the 29th of each month - the active window and closing date are displayed at the top of the Wallet page.' },
    { title: 'BEFORE YOU START', body: 'Ensure you have a positive Available to Withdraw balance on at least one trading account.\nEnsure your account is in good standing with no open compliance reviews.' },
    { title: 'STEPS', body: 'Go to the Wallet section and click + Request Payout.\nSelect the source account you wish to withdraw from.\nEnter the amount. The minimum withdrawal is $500 and the maximum is your Available to Withdraw balance per account.\nClick Next to proceed to the confirmation screen.\nReview the breakdown - this includes your requested amount, the Rise fee (-$50), and your net payout.\nClick Confirm to submit.\nYou will receive updates via email. You can also track the status of your withdrawal (paid, processing, or denied) under Withdrawal History at the bottom of the Wallet page.' },
    { title: 'NOTE', body: 'A $50 Rise fee is charged per withdrawal and is deducted from your requested amount.' },
  ],
  'What is available to withdraw?': [
    { title: 'OVERVIEW', body: 'Available to Withdraw is the amount of your realized profit currently eligible for payout. Two conditions must be met for a withdrawal to be approved.' },
    { title: 'BALANCE MUST EXCEED YOUR STARTING BALANCE', body: 'You can only withdraw profits, not the funded capital itself. If your balance is at or below your starting balance, your Available to Withdraw is $0.' },
    { title: 'PROJECTED EQUITY MUST STAY ABOVE THE RISK COVER THRESHOLD', body: 'The risk cover threshold is set at 95% of your starting account balance (e.g. $95,000 on a $100,000 account). If your projected equity after withdrawal falls at or below this threshold, the request will be rejected or the withdrawable amount reduced.' },
    { title: 'EXAMPLE', body: 'Current balance: $105,000\nCurrent equity (after open losses): $95,200\nRequested withdrawal: $3,000\nProjected equity after withdrawal: $92,200\nRisk cover threshold: $95,000\nSince $92,200 is below the $95,000 threshold, this request would be rejected.' },
    { title: 'NOTE', body: 'This check runs at submission and continues to apply while your request is pending. If your equity drops further before the request is processed, it may be automatically rejected.' },
  ],
  'What is Cumulative Profit?': [
    { title: 'OVERVIEW', body: "Cumulative Profit is the total net profit your DFY account has generated since activation - the sum of all closed trade profits and losses over the account's lifetime." },
    { title: 'WHAT COUNTS TOWARD CUMULATIVE PROFIT?', body: 'Realized gains and losses from closed positions.\nSwap charges applied to overnight positions.\nOpen or floating positions do not count until they are closed.' },
  ],
  'What is Net Withdrawals?': [
    { title: 'OVERVIEW', body: 'Net Withdrawals is the running total of all payouts you have received, calculated after your 80% profit share split and any applicable Rise fees.' },
    { title: 'HOW IT IS CALCULATED', body: 'When you request a payout, two deductions apply before you receive it:\nProfit share split: 80% of the realized profit is yours; 20% is retained by the platform.\nRise fee: $50 per withdrawal.\nThe resulting amount is what counts toward your Net Withdrawals figure.' },
  ],
  'Why was my payout denied?': [
    { title: 'OVERVIEW', body: 'Payout requests may be denied or held for a number of reasons. If your payout was not approved, one or more of the following may apply.' },
    { title: 'COMMON REASONS FOR DENIAL', body: 'Chargeback or payment dispute: Any active chargeback on your account will result in an immediate payout freeze.\nCompliance review: Your account may have been selected for a routine review. This does not necessarily indicate a violation.\nTechnical issue: In rare cases, payment processing failures may cause a temporary delay. These are typically resolved automatically.' },
    { title: 'WHAT TO DO', body: 'Contact support with your account ID and payout request reference. The team will review your case and communicate an outcome.\nImportant: Accounts found in violation of program rules may be permanently terminated with forfeiture of pending payouts under the Terms of Service.' },
  ],
  'How long do payouts take?': [
    { title: 'OVERVIEW', body: 'Payout processing involves two stages: an internal review and an on-chain crypto transfer.' },
    { title: 'REVIEW STAGE', body: '1-2 business days from submission.' },
    { title: 'TRANSFER STAGE', body: 'Once approved, crypto transfers are typically dispatched within 24 hours. On-chain confirmation usually takes minutes to a few hours, depending on network congestion.\nTotal end-to-end time is typically 2-5 business days from submission.' },
    { title: 'WHAT CAN CAUSE DELAYS?', body: 'High volume of pending requests.\nAccount flagged for compliance review.\nNetwork congestion on the blockchain used for transfer.\nIncorrect or missing wallet address.\nIf your payout has been pending for more than 5 business days with no update, contact support.' },
  ],
  'Are there any payout fees?': [
    { title: 'OVERVIEW', body: 'POW does not charge a platform fee for payout requests. However, there are a few costs to be aware of.' },
    { title: 'NETWORK (GAS) FEES', body: 'Crypto payouts are subject to standard blockchain network fees at the time of transfer. These are not charged by POW - they are paid to validators on the underlying network.' },
    { title: 'WHAT YOU RECEIVE', body: 'The amount you see in your Available to Withdraw is your gross payout. The small network fee is deducted at the point of on-chain transfer, so your final received amount may be marginally less than the requested amount.' },
  ],

  // ── Leaderboard ──
  'How are leaderboard rankings calculated?': [
    { title: 'OVERVIEW', body: 'Your leaderboard rank is determined by your Total AUM - the combined funded allocation across all of your active accounts. The trader with the highest Total AUM holds the top position.' },
    { title: 'PLATFORM-WIDE METRICS', body: 'The three summary metrics at the top of the leaderboard show platform-wide figures across all traders:\nTotal AUM: Combined funded allocation across every active account on the platform.\nActive Accounts: Total number of currently active funded accounts across all users.\nNet Withdrawals: Total payouts processed across the platform to date.' },
    { title: 'PER-TRADER METRICS', body: 'The leaderboard table also shows two per-trader metrics:\nNet Withdrawals: Your personal total payouts received to date, after profit share and fees.\nReturn on Fees: Your profit generated relative to total activation fees paid, expressed as a percentage.\nThese columns can be sorted by clicking the arrow next to each header, but they do not affect your rank. Rank is determined solely by Total AUM.' },
  ],
  'What is Assets Under Management (AUM)?': [
    { title: 'OVERVIEW', body: 'AUM refers to the total funded allocation across all of your active DFY accounts - the total notional account size POW has made available to you.' },
    { title: 'HOW AUM AFFECTS YOUR RANK', body: 'AUM is the sole determinant of your leaderboard rank. The only way to grow your AUM is by activating additional funded accounts.' },
    { title: 'NOTE', body: 'AUM only reflects currently active accounts. Liquidated or closed accounts do not count toward your total.' },
  ],
  'What is Return on Fees?': [
    { title: 'OVERVIEW', body: 'Return on Fees (RoF) measures how much profit you have generated relative to the total Activation Payments you have paid.' },
    { title: 'FORMULA', body: 'Return on Fees = Cumulative Profit ÷ Total Activation Payments Paid' },
    { title: 'WHY IT MATTERS', body: 'This metric rewards capital efficiency and is particularly useful for traders with smaller allocations competing at the top of the leaderboard.' },
  ],
  'Why did my rank change?': [
    { title: 'OVERVIEW', body: 'Rankings are relative - they reflect your position compared to all other active traders, not just your own numbers.' },
    { title: 'COMMON REASONS YOUR RANK MAY DROP', body: 'Other traders grew their AUM faster than you.\nA drawdown event reduced your Cumulative Profit.\nAccount inactivity.' },
    { title: 'COMMON REASONS YOUR RANK MAY RISE', body: 'Increased profit generation.\nCompetitors experiencing drawdown events or going inactive.\nScaling up your AUM through additional accounts.' },
  ],
  'How often does the leaderboard update?': [
    { title: 'OVERVIEW', body: 'The leaderboard refreshes every 5 minutes, so rankings reflect near real-time changes in Total AUM.' },
    { title: 'WHY RANKINGS MAY APPEAR UNCHANGED', body: 'If no new accounts have been activated or closed within the update window, rankings may remain stable between refreshes.' },
    { title: 'NOTE', body: 'Your rank can change at any time as other traders activate or lose accounts. Check back regularly during active periods.' },
  ],
  'Can I hide my profile from the leaderboard?': [
    { title: 'OVERVIEW', body: 'No. All active traders are visible on the leaderboard and profiles cannot be hidden. The leaderboard is fully transparent across all users.' },
  ],

  // ── Trade Copier ──
  'What is the Trade Copier?': [
    { title: 'OVERVIEW', body: 'The Trade Copier automatically mirrors trades from a master account into your DFY funded account. When the master opens, modifies, or closes a position, those actions are replicated in your account in real time.' },
    { title: 'AUTOMATIC SETUP', body: 'When your account is synced for the first time, it is automatically connected to the Trade Copier using the master you selected during checkout. No manual setup is required.' },
    { title: 'KEY CONCEPTS', body: 'Master account: The source account whose trades are being copied into your account.\nCopy multiplier: A scaling factor that adjusts lot sizes when copying. The default is 1x (lot-for-lot).' },
    { title: 'CHANGING YOUR MASTER', body: 'Open a help ticket with your trading account ID and the name of the master you would like to switch to. The team will process the change on your behalf.' },
  ],
  'How do I connect a slave account?': [
    { title: 'OVERVIEW', body: 'Your trading accounts are automatically connected to the Trade Copier using the master you selected during checkout. No manual setup is required for your initial connection.' },
    { title: 'RECONNECTING A DISCONNECTED ACCOUNT', body: 'Go to the Trade Copier section in your portal.\nClick + Add Slave in the top right corner.\nSelect a master from the available options. Each master displays its win rate, growth, and track record to help you choose.\nSelect the slave account you wish to connect. Only accounts already synced to your portal will appear.\nClick Continue to confirm.' },
  ],
  'How do I add a master account?': [
    { title: 'OVERVIEW', body: 'You cannot add external master accounts to the Trade Copier. Masters are preloaded by POW and represent the available strategies you can follow.' },
    { title: 'SELECTING A MASTER', body: 'When setting up your account, you select a master during checkout and your account is automatically connected upon activation. Available masters can be viewed in the Trade Copier section, each displaying their win rate, growth, and track record.' },
    { title: 'WANT TO SWITCH MASTERS?', body: 'Go to the Trade Copier section, click Manage on the relevant account, then click Switch. Open a ticket with the topic Switch Master, include your trading account ID and the name of the master you would like to switch to, and the team will process the change.' },
  ],
  'How does the copy multiplier work?': [
    { title: 'OVERVIEW', body: 'The copy multiplier controls how lot sizes from the master are scaled when copied to your account. For example, if the master opens a 1 lot trade and your multiplier is set to 2.4x, the trade will be copied as 2.40 lots on your account.' },
    { title: 'AVAILABILITY', body: '$100,000 accounts: The multiplier is fixed and cannot be adjusted.\n$200,000 accounts and above: The multiplier is enabled and can be changed at any time by clicking Manage next to the relevant account.' },
    { title: 'ADJUSTING THE MULTIPLIER', body: 'Use the slider to set your desired multiplier and click Save multiplier. You will be asked to confirm before the change takes effect. The new multiplier applies to new trades only - positions already open at the time of the change will keep their current lot size.' },
    { title: 'WARNING', body: 'Increasing the multiplier means larger copied positions and greater exposure. Review your risk tolerance carefully before saving.' },
  ],
  'How do I switch masters?': [
    { title: 'OVERVIEW', body: 'Switching masters is handled by the support team and cannot be done directly from the portal.' },
    { title: 'STEPS', body: 'Go to the Trade Copier section and click Manage next to the account you wish to switch.\nClick the Switch button next to the master name.\nYou will be redirected to raise a support ticket. Include your trading account ID and the name of the master you would like to switch to.\nThe team will process the change and notify you once completed.' },
    { title: 'NOTE', body: 'Review the available masters in the Trade Copier section before submitting your request. Each master displays its win rate, growth, and track record.' },
  ],
  'What happens if my master account disconnects?': [
    { title: 'OVERVIEW', body: 'If the master loses its connection to the copier, trade signals will stop being received by your account. The status in the Trade Copier section will update to Disconnected and you will receive an email notification.' },
    { title: 'NO ACTION REQUIRED', body: 'The system will automatically re-attempt the connection until it is restored.' },
    { title: 'WHAT HAPPENS TO OPEN TRADES?', body: 'Trades already open at the time of disconnection are not automatically closed. They remain open and continue to accumulate P&L independently until closed by you or the risk system. Once disconnected, your open trades are no longer mirrored against the master and may diverge until the connection is restored.' },
    { title: 'MONITORING THE CONNECTION', body: 'You can check connectivity status at any time in the Trade Copier section under the Status column. If the disconnection persists, contact support.' },
  ],
  'Why is my copier showing an error?': [
    { title: 'OVERVIEW', body: 'An Error status means the connection or trade execution between your account and the master has encountered a problem.' },
    { title: 'POSSIBLE ERROR TYPES', body: "Application error: A broker-level error was returned during trade execution.\nTrade not found: A trade could not be located by its ID in the system.\nCancelled by copier: A trade was cancelled by the copier before it could be executed.\nSymbol not found: The instrument being traded by the master could not be matched to your account.\nOut of range: The open price exceeded the slippage setting at the time of execution.\nLot size below minimum: The copied lot size is below your broker's minimum allowed lot size.\nNo valid filling mode: No valid order filling mode could be applied (MT5-level error).\nInvalid risk configuration: The copier has detected a problem with the risk settings on your account." },
    { title: 'WHAT TO DO', body: 'All of the above require action from the support team. Raise a ticket with your trading account ID and the team will investigate.' },
  ],
  'How is risk managed when copying trades?': [
    { title: 'OVERVIEW', body: "The Trade Copier does not override your account's standard risk controls. All DFY program risk rules remain fully in force." },
    { title: 'DFY DRAWDOWN LIMIT', body: 'Your account is subject to the same 7.2% maximum drawdown limit as any other DFY account. If this threshold is reached, positions will be liquidated and the account closed - regardless of what the master account is doing.' },
    { title: 'COPY MULTIPLIER RISK', body: "For accounts of $200,000 and above, a higher multiplier means each copied trade consumes more margin and can move your drawdown faster than the master's own account." },
    { title: 'STOPS AND LIMITS', body: 'Stop losses and take profits set on the master are copied to your account at the scaled lot size. If the master removes or modifies these, the changes are mirrored on your account.' },
    { title: 'WARNING', body: 'A master trading within its own risk limits may still cause your account to breach drawdown if the multiplier is scaled up aggressively. Always review your risk tolerance before increasing the multiplier.' },
  ],
  'Can I copy multiple masters at once?': [
    { title: 'OVERVIEW', body: 'Each account can only follow one master at a time. However, if you have multiple funded accounts, each can be connected to a different master independently, allowing you to run multiple strategies in parallel.' },
    { title: 'AVAILABLE MASTERS', body: 'Available masters include Foundry, Polaris, Meridian, Atlas, Apex, and Bullion.' },
    { title: 'EXAMPLE SETUP', body: 'POW DFY #500 → Foundry (1.0x multiplier)\nPOW DFY #501 → Meridian (1.0x multiplier)' },
    { title: 'NOTE', body: 'Each account has its own independent 7.2% drawdown limit. A drawdown event on one account does not affect another, but you should monitor all accounts independently.' },
  ],
  'How do I disconnect a copied account?': [
    { title: 'STEPS', body: 'Go to the Trade Copier section and click Manage next to the account you want to disconnect.\nClick Disconnect this account at the bottom of the management panel.\nConfirm the action. Copy trading will be paused and the status will update to Disconnected.' },
    { title: 'WHAT HAPPENS AFTER DISCONNECTING?', body: 'Existing open trades are not closed automatically. They remain open and must be managed manually. No new trades will be copied until the account is reconnected.' },
    { title: 'RECONNECTING', body: 'Click + Add Slave in the Trade Copier section and follow the prompts to reconnect to any available master.' },
    { title: 'NOTE', body: 'If you want to switch to a different master without disconnecting first, this must be done through support. See "How do I switch masters?" for details.' },
  ],
};
const DEFAULT_STEPS = [{ title: 'OVERVIEW', body: 'This article is currently being prepared. Check back soon for more details.' }];

const EMPTY_REPORT  = { category: '', description: '' };

/* ── Search ──
   Every article once, with the text it is searched on: its title, then its
   body. Built from the category lists so each carries the category it opens
   under; the popular list adds nothing new, but is folded in so an article
   only listed there would still be found. */
const SEARCHABLE_ARTICLES = (() => {
  const seen = new Map();
  const add = (title, catId) => {
    if (seen.has(title)) return;
    const body = (ARTICLE_STEPS[title] || []).map(st => `${st.title} ${st.body}`).join(' ');
    seen.set(title, { title, catId, titleText: title.toLowerCase(), text: `${title} ${body}`.toLowerCase() });
  };
  Object.entries(CATEGORY_ARTICLES).forEach(([catId, titles]) => titles.forEach(t => add(t, catId)));
  POPULAR_ARTICLES.forEach(({ title, catId }) => add(title, catId));
  return [...seen.values()];
})();

/* Every word of the query has to appear somewhere, in any order — "payout
   fee" finds "Are there any payout fees?". Title matches rank first; within
   each group the order is the page's own. */
function searchItems(items, query, getTitle, getText) {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const hits = items.filter(it => words.every(w => getText(it).includes(w)));
  const inTitle = it => words.every(w => getTitle(it).includes(w));
  return [...hits.filter(inTitle), ...hits.filter(it => !inTitle(it))];
}

/* The Add video tutorial form's limits. The API holds the same ones. */
const TUTORIAL_TITLE_MAX       = 80;
const TUTORIAL_DESCRIPTION_MAX = 1000;
const EMPTY_TUTORIAL = { title: '', description: [], videoUrl: '' };
const TICKETS_PER_PAGE = 5;
const MAX_ADDITIONAL_DETAILS = 2;

function relativeTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fullDate(dateStr) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
}

function ticketId(id) {
  return 'INC-' + String(1000 + (parseInt(String(id).slice(-4), 16) % 9000));
}

function formatTicket(conv) {
  return {
    _id:           conv._id,
    id:            ticketId(conv._id),
    issue:         conv.subject,
    type:          conv.category,
    category:      conv.category,
    createdAt:     conv.createdAt,
    submitted:     relativeTime(conv.createdAt),
    submittedFull: fullDate(conv.createdAt),
    status:        conv.status === 'unsolved' ? 'In progress' : 'Resolved',
  };
}

function StatusBadge({ status }) {
  const cls = { 'In progress': styles.statusInProgress, Resolved: styles.statusResolved, Connected: styles.statusConnected, Sent: styles.statusSent }[status] || '';
  return <span className={`${styles.statusBadge} ${cls}`}><span className={styles.statusDot} /><span className={styles.statusText}>{status}</span></span>;
}

function HelpCenter() {
  usePageTitle('Help Center');
  // Admins add video tutorials; the API checks this again on the way in.
  const { isAdmin } = JSON.parse(localStorage.getItem('user') || '{}');
  const [searchParams, setSearchParams] = useSearchParams();

  /* Where you are in the Help Center lives in the URL, not in state, so every
     category and article is a real address: Back returns to the previous Help
     Center screen instead of leaving the page, and a link can be shared or
     reloaded. */
  const currentArticle = searchParams.get('article');
  const currentTutorialId = searchParams.get('tutorial');
  /* An unrecognised category (a mistyped or stale link) falls back to the home
     view rather than rendering an empty screen. */
  const currentCat     = FAQ_CATEGORIES.find(c => c.id === searchParams.get('category'));
  const currentCatId   = currentCat?.id ?? null;
  const view = currentTutorialId ? 'tutorial'
    : currentArticle ? 'article'
    : currentCat ? 'category'
    : 'main';

  /* Published video tutorials, in the order the desk added them. Empty until
     loaded, and on failure too: the section only shows when there is
     something in it. */
  const [tutorials, setTutorials]           = useState([]);
  const [tutorialsLoaded, setTutorialsLoaded] = useState(false);
  const currentTutorial = tutorials.find(t => t._id === currentTutorialId) || null;

  /* ── Add video tutorial (admins) ── */
  const [tutorialForm, setTutorialForm]       = useState(EMPTY_TUTORIAL);
  // The link is only called out once the admin has left the field or tried to
  // submit — not on the first character of a paste still being typed.
  const [videoTouched, setVideoTouched]       = useState(false);
  const [addingTutorial, setAddingTutorial]   = useState(false);
  const [tutorialError, setTutorialError]     = useState('');
  const videoLinkInvalid  = !!tutorialForm.videoUrl.trim() && !isVideoUrl(tutorialForm.videoUrl);
  const tutorialComplete  = !!tutorialForm.title.trim()
    && !isBlankDocument(tutorialForm.description)
    && !!tutorialForm.videoUrl.trim();
  const canAddTutorial    = tutorialComplete && !videoLinkInvalid
    && textLength(tutorialForm.description) <= TUTORIAL_DESCRIPTION_MAX
    && !addingTutorial;

  const [ticketTab, setTicketTab]     = useState('open');
  const [ticketFilterOpen, setTicketFilterOpen] = useState(false);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [search, setSearch]           = useState('');
  const [sortDir, setSortDir]         = useState('desc');

  const [openTickets, setOpenTickets]         = useState([]);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [loadingTickets, setLoadingTickets]   = useState(true);
  const [refreshing, setRefreshing]           = useState(false);

  const [modal, setModal]                     = useState(null);
  const [selectedTicket, setSelectedTicket]   = useState(null);
  const [ticketMessages, setTicketMessages]   = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [reportForm, setReportForm]         = useState(EMPTY_REPORT);
  const [reportErrors, setReportErrors]     = useState({});
  const [reportedTicket, setReportedTicket] = useState(null);
  const [submitting, setSubmitting]         = useState(false);

  const [updateText, setUpdateText]     = useState('');
  const [reportFile, setReportFile]     = useState(null);
  const [updateFile, setUpdateFile]     = useState(null);
  const [addingUpdate, setAddingUpdate] = useState(false);
  const [draggingReport, setDraggingReport] = useState(false);
  const [draggingUpdate, setDraggingUpdate] = useState(false);

  const reportFileRef = useRef(null);
  const updateFileRef = useRef(null);
  const ticketFilterRef = useRef(null);

  const tickets    = ticketTab === 'open' ? openTickets : resolvedTickets;
  const sorted = [...tickets].sort((a, b) => {
    const diff = new Date(a.createdAt) - new Date(b.createdAt);
    return sortDir === 'desc' ? -diff : diff;
  });
  // The empty table says which nothing this is: no open tickets, or a user who
  // has never filed one.
  const emptyTicketsMessage = ticketTab === 'open' ? 'No open tickets :)' : 'No support history';

  /* The search box searches what it says — the help articles, and the video
     tutorials beside them — and while it holds a query its results stand in
     for the rest of the home view. */
  const query = search.trim();
  const articleResults = query
    ? searchItems(SEARCHABLE_ARTICLES, query, a => a.titleText, a => a.text)
    : [];
  const tutorialResults = query
    ? searchItems(
        tutorials, query,
        t => t.title.toLowerCase(),
        t => `${t.title} ${plainText(t.description)}`.toLowerCase(),
      )
    : [];
  const totalPages   = Math.ceil(sorted.length / TICKETS_PER_PAGE);
  const pagedTickets = sorted.slice((ticketsPage - 1) * TICKETS_PER_PAGE, ticketsPage * TICKETS_PER_PAGE);
  const steps        = currentArticle ? (ARTICLE_STEPS[currentArticle] || DEFAULT_STEPS) : [];
  // Support replies (Slack sync or admin portal) don't count against the user's update limit.
  const additionalDetailsCount = Math.max(0, ticketMessages.filter(m => !m.fromSupport).length - 1);
  const canAddUpdate = !!selectedTicket
    && selectedTicket.status !== 'Resolved'
    && additionalDetailsCount < MAX_ADDITIONAL_DETAILS;

  useEffect(() => { fetchConversations(); }, []);

  useEffect(() => {
    api.get('/help/tutorials')
      .then(({ data }) => setTutorials(Array.isArray(data) ? data : []))
      .catch(() => setTutorials([]))
      .finally(() => setTutorialsLoaded(true));
  }, []);

  /* Reset the scroll on every move between Help Center screens — keyed on the URL
     so Back and Forward land at the top of the destination too, not part-way down
     wherever the previous screen was scrolled to. */
  useEffect(() => { scrollToTop(); }, [currentCatId, currentArticle, currentTutorialId]);

  useEffect(() => {
    if (!ticketFilterOpen) return;
    function handleClickOutside(e) {
      if (ticketFilterRef.current && !ticketFilterRef.current.contains(e.target)) {
        setTicketFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ticketFilterOpen]);

  useEffect(() => {
    if (searchParams.get('action') === 'permanent-access') {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setReportForm({
        category:    'Other',
        description: `I am the recovery contact for ${user.firstName ? user.firstName + "'s" : 'this'} account and I would like to request permanent access.`,
        action:      'permanent-access',
      });
      setModal('report');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link from the Trade Copier "Switch" button — open a "Switch master"
  // ticket prefilled under the Trade Copier category.
  useEffect(() => {
    if (searchParams.get('action') === 'switch-master') {
      setReportForm({
        category:    'Trade Copier',
        description: 'Switch master: I would like to switch the master account my copier is following. Trading account ID: . Master I would like to switch to: .',
      });
      setModal('report');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (modal === 'details' && selectedTicket?._id) {
      fetchMessages(selectedTicket._id);
    }
  }, [modal, selectedTicket]);

  async function fetchConversations({ quiet = false } = {}) {
    if (quiet) setRefreshing(true);
    else setLoadingTickets(true);
    try {
      const res = await api.get('/help/conversations');
      const all = res.data.map(formatTicket);
      setOpenTickets(all.filter(t => t.status === 'In progress'));
      setResolvedTickets(all.filter(t => t.status === 'Resolved'));
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      if (quiet) setRefreshing(false);
      else setLoadingTickets(false);
    }
  }

  async function fetchMessages(conversationId) {
    setLoadingMessages(true);
    setTicketMessages([]);
    try {
      const res = await api.get(`/help/messages/${conversationId}`);
      setTicketMessages(res.data);
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setLoadingMessages(false);
    }
  }

  function scrollToTop() {
    document.querySelector('main')?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }
  /* Each of these pushes a history entry, so Back walks article → category →
     help center home before it ever leaves the page. Only the two navigation
     params are carried over — a one-shot `action` deep link has already opened
     its modal by now and must not fire again when the user navigates back. */
  function goToCategory(catId) { setSearchParams({ category: catId }); }
  function goToArticle(title, catId) {
    const cat = catId || currentCatId;
    setSearchParams(cat ? { category: cat, article: title } : { article: title });
  }
  function goToTutorial(id) { setSearchParams({ tutorial: id }); }
  function goHome() { setSearchParams({}); }

  /* There is no page listing the tutorials on their own — they live in a
     section of the home view — so the breadcrumb's "Video tutorials" goes home
     and brings that section into view. */
  function goToTutorials() {
    setSearchParams({});
    requestAnimationFrame(() => {
      document.getElementById('video-tutorials')?.scrollIntoView({ block: 'start' });
    });
  }

  function openReport(catId) {
    const label = catId ? (FAQ_CATEGORIES.find(c => c.id === catId)?.label || '') : '';
    setReportForm({ ...EMPTY_REPORT, category: label });
    setReportErrors({});
    setReportFile(null);
    setModal('report');
  }

  async function submitReport() {
    const errs = {};
    if (!reportForm.category)           errs.category    = 'Please select a category';
    if (!reportForm.description.trim()) errs.description = 'Please describe the issue';
    if (Object.keys(errs).length) { setReportErrors(errs); return; }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category', reportForm.category);
      fd.append('description', reportForm.description);
      if (reportForm.action) fd.append('action', reportForm.action);
      if (reportFile) fd.append('attachment', reportFile);

      const res = await api.post('/help/conversations', fd);
      const ticket = formatTicket(res.data.conversation);
      setReportedTicket({
        ...ticket,
        status:      'Sent',
        description: reportForm.description,
        attachment:  reportFile?.name || null,
      });
      setModal('reported');
      fetchConversations();
    } catch (err) {
      console.error('Failed to submit ticket', err);
    } finally {
      setSubmitting(false);
    }
  }

  async function addUpdate() {
    if (!canAddUpdate) return;
    if (!updateText.trim() && !updateFile) return;
    setAddingUpdate(true);
    try {
      const fd = new FormData();
      fd.append('message', updateText.trim() || '(attachment)');
      if (updateFile) fd.append('attachment', updateFile);
      await api.post(`/help/messages/${selectedTicket._id}`, fd);
      setUpdateText('');
      setUpdateFile(null);
      fetchMessages(selectedTicket._id);
    } catch (err) {
      console.error('Failed to add update', err);
    } finally {
      setAddingUpdate(false);
    }
  }

  function handleDrop(e, setFile, setDragging) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setFile(file);
  }

  function openAddTutorial() {
    setTutorialForm(EMPTY_TUTORIAL);
    setVideoTouched(false);
    setTutorialError('');
    setModal('tutorial');
  }

  async function submitTutorial() {
    setVideoTouched(true);
    if (!canAddTutorial) return;
    setAddingTutorial(true);
    setTutorialError('');
    try {
      const { data } = await api.post('/help/tutorials', {
        title:       tutorialForm.title.trim(),
        description: tutorialForm.description,
        videoUrl:    tutorialForm.videoUrl.trim(),
      });
      // Newest last — the same order the list is served in.
      setTutorials(list => [...list, data]);
      closeModal();
    } catch (err) {
      setTutorialError(err.response?.data?.error || 'Failed to add the tutorial. Please try again.');
    } finally {
      setAddingTutorial(false);
    }
  }

  function closeModal() {
    setModal(null);
    // Cancel and X both discard the tutorial being written.
    setTutorialForm(EMPTY_TUTORIAL);
    setTutorialError('');
    setSelectedTicket(null);
    setTicketMessages([]);
    setUpdateText('');
    setUpdateFile(null);
    setReportFile(null);
  }

  return (
    <DashboardLayout>
      <div className={styles.page}>

        {/* ── MAIN VIEW ── */}
        {view === 'main' && (<>
          <div className={styles.header}>
            <div>
              <h1 className={styles.pageTitle}>Help center</h1>
              <p className={styles.pageSubtitle}>Get help with your account and track support requests</p>
            </div>
            <Button variant="primary" size="sm" fullWidth={false} className={styles.ctaBtn} onClick={() => openReport(null)}>
              <Plus size={16} /> Report an issue
            </Button>
          </div>

          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search help articles"
            className={styles.searchWrap}
          />

          {query ? (<>
            {articleResults.length === 0 && tutorialResults.length === 0 ? (
              <div className={styles.searchEmpty}>
                <p className={styles.searchEmptyTitle}>No results for &ldquo;{query}&rdquo;</p>
                <p className={styles.stillText}>
                  Try different words, or <button className={styles.reportLink} onClick={() => openReport(null)}>report an issue</button> and our team will help.
                </p>
              </div>
            ) : (<>
              {articleResults.length > 0 && (<>
                <p className={styles.sectionLabel}>ARTICLES ({articleResults.length})</p>
                <div className={styles.popularGrid}>
                  {articleResults.map(({ title, catId }) => (
                    <button key={title} className={styles.popularItem} onClick={() => goToArticle(title, catId)}>
                      <span className={styles.resultText}>
                        <span>{title}</span>
                        <span className={styles.resultMeta}>{FAQ_CATEGORIES.find(c => c.id === catId)?.label}</span>
                      </span>
                      <ArrowRight size={16} strokeWidth={1.33} className={styles.popularArrow} />
                    </button>
                  ))}
                </div>
              </>)}
              {tutorialResults.length > 0 && (<>
                <p className={styles.sectionLabel}>VIDEO TUTORIALS ({tutorialResults.length})</p>
                <div className={styles.popularGrid}>
                  {tutorialResults.map(t => (
                    <button key={t._id} className={styles.popularItem} onClick={() => goToTutorial(t._id)}>
                      <span>{t.title}</span>
                      <ArrowRight size={16} strokeWidth={1.33} className={styles.popularArrow} />
                    </button>
                  ))}
                </div>
              </>)}
            </>)}
          </>) : (<>

          <div className={styles.sectionRow}>
            <div className={styles.sectionLabelGroup}>
              <p className={styles.sectionLabel}>SUPPORT TICKETS</p>
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={() => fetchConversations({ quiet: true })}
                disabled={refreshing || loadingTickets}
                title="Refresh ticket status"
                aria-label="Refresh ticket status"
              >
                <RefreshCw size={15} className={refreshing ? styles.refreshSpin : ''} />
              </button>
            </div>
            <div className={styles.ticketTabs}>
              <button className={`${styles.tab} ${ticketTab === 'open' ? styles.tabActive : ''}`} onClick={() => { setTicketTab('open'); setTicketsPage(1); }}>Open ({openTickets.length})</button>
              <button className={`${styles.tab} ${ticketTab === 'resolved' ? styles.tabActive : ''}`} onClick={() => { setTicketTab('resolved'); setTicketsPage(1); }}>Resolved ({resolvedTickets.length})</button>
            </div>
            <div className={styles.ticketFilter} ref={ticketFilterRef}>
              <button
                type="button"
                className={styles.ticketFilterBtn}
                onClick={() => setTicketFilterOpen(o => !o)}
              >
                {ticketTab === 'open' ? `Open (${openTickets.length})` : `Resolved (${resolvedTickets.length})`}
                <ChevronDown size={16} />
              </button>
              {ticketFilterOpen && (
                <div className={styles.ticketFilterMenu}>
                  <button
                    type="button"
                    className={styles.ticketFilterItem}
                    onClick={() => { setTicketTab('open'); setTicketsPage(1); setTicketFilterOpen(false); }}
                  >
                    Open ({openTickets.length})
                  </button>
                  <button
                    type="button"
                    className={styles.ticketFilterItem}
                    onClick={() => { setTicketTab('resolved'); setTicketsPage(1); setTicketFilterOpen(false); }}
                  >
                    Resolved ({resolvedTickets.length})
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.tableScroll}>
              {/* With no rows to label, the header is dead weight on a narrow
                  screen — mobile drops it and shows only the empty message. */}
              <table className={`${styles.table} ${pagedTickets.length === 0 ? styles.tableEmpty : ''}`}>
                <thead>
                  <tr>
                    <th className={styles.th}>Issue</th>
                    <th className={`${styles.th} ${styles.colCategory}`}>Category</th>
                    <th className={`${styles.th} ${styles.colSubmitted}`}>
                      <button
                        type="button"
                        className={styles.sortBtn}
                        onClick={() => { setSortDir(d => d === 'desc' ? 'asc' : 'desc'); setTicketsPage(1); }}
                      >
                        Submitted
                        {sortDir === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                      </button>
                    </th>
                    <th className={styles.th}>Status</th>
                    <th className={`${styles.th} ${styles.right}`}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTickets ? (
                    <tr><td colSpan={5} className={`${styles.td} ${styles.emptyRow}`}>Loading tickets…</td></tr>
                  ) : pagedTickets.length === 0 ? (
                    <tr><td colSpan={5} className={`${styles.td} ${styles.emptyRow}`}>{emptyTicketsMessage}</td></tr>
                  ) : pagedTickets.map(t => (
                    <tr key={t._id} className={styles.tr}>
                      <td className={styles.td}><span className={styles.issueTitle}>{t.issue}</span><span className={styles.issueType}>{t.type}</span></td>
                      <td className={`${styles.td} ${styles.colCategory}`}><strong>{t.category}</strong></td>
                      <td className={`${styles.td} ${styles.colSubmitted}`}>{t.submitted}</td>
                      {/* 600px, not the usual 767: the label is only hidden
                          inside this table, and only at that width. */}
                      <td className={styles.td}>
                        <StatusTooltip label={t.status} maxWidth={600}>
                          <StatusBadge status={t.status} />
                        </StatusTooltip>
                      </td>
                      <td className={`${styles.td} ${styles.right}`}>
                        <button className={styles.detailsBtn} onClick={() => { setSelectedTicket(t); setModal('details'); }}>See details</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && <Pagination page={ticketsPage} totalPages={totalPages} onPageChange={setTicketsPage} variant="outline" />}
          </div>

          <p className={styles.sectionLabel}>FREQUENTLY ASKED QUESTIONS</p>
          <div className={styles.faqGrid}>
            {FAQ_CATEGORIES.map(({ id, Icon, label, count }) => (
              <button key={id} className={styles.faqCard} onClick={() => goToCategory(id)}>
                <div className={styles.faqCardTop}>
                  <div className={styles.faqIconBox}><Icon size={18} /></div>
                  <span className={styles.faqCardTitle}>{label}</span>
                </div>
                <div className={styles.faqCardBottom}>
                  <span className={styles.faqCardCount}>{count} articles</span>
                  <ArrowRight size={15} className={styles.faqArrow} />
                </div>
              </button>
            ))}
          </div>

          <p className={styles.sectionLabel}>POPULAR ARTICLES</p>
          <div className={styles.popularGrid}>
            {POPULAR_ARTICLES.map(({ title, catId }) => (
              <button key={title} className={styles.popularItem} onClick={() => goToArticle(title, catId)}>
                <span>{title}</span>
                <ArrowRight size={16} strokeWidth={1.33} className={styles.popularArrow} />
              </button>
            ))}
          </div>

          {/* Same cards as the articles above. Nothing at all when no tutorial
              is published — not even the heading — except for an admin, who
              needs the heading's Add new to publish the first one. */}
          {(tutorials.length > 0 || isAdmin) && (<>
            <div id="video-tutorials" className={styles.sectionRow}>
              <p className={styles.sectionLabel}>VIDEO TUTORIALS</p>
              {isAdmin && (
                <button type="button" className={styles.addNewLink} onClick={openAddTutorial}>
                  <Plus size={16} /> <span>Add new</span>
                </button>
              )}
            </div>
            {tutorials.length === 0 && (
              <p className={styles.tutorialsEmpty}>No video tutorials yet. Users won&rsquo;t see this section until one is added.</p>
            )}
            <div className={styles.popularGrid}>
              {tutorials.map(t => (
                <button key={t._id} className={styles.popularItem} onClick={() => goToTutorial(t._id)}>
                  <span>{t.title}</span>
                  <ArrowRight size={16} strokeWidth={1.33} className={styles.popularArrow} />
                </button>
              ))}
            </div>
          </>)}
          </>)}

          <PageFooter />
        </>)}

        {/* ── CATEGORY VIEW ── */}
        {view === 'category' && currentCat && (<>
          <nav className={styles.breadcrumb}>
            <button className={styles.breadcrumbLink} onClick={goHome}>Help center</button>
            <ChevronRight size={16} className={styles.breadcrumbSep} />
            <span className={styles.breadcrumbCurrent}>{currentCat.label}</span>
          </nav>

          <div className={styles.catHeader}>
            <div className={styles.catHeaderTop}>
              <div className={styles.catLeft}>
                <div className={styles.catIconBox}><currentCat.Icon size={22} /></div>
                <h1 className={styles.catTitle}>{currentCat.label}</h1>
              </div>
              <Button variant="primary" size="sm" fullWidth={false} className={styles.ctaBtn} onClick={() => openReport(currentCat.id)}>
                <Plus size={16} /> Report {currentCat.label} issue
              </Button>
            </div>
            <p className={styles.catDesc}>{currentCat.desc}</p>
          </div>

          <p className={styles.sectionLabel}>ARTICLES</p>
          <div className={styles.articlesGrid}>
            {(CATEGORY_ARTICLES[currentCatId] || []).map((title, i) => (
              <button key={i} className={styles.articleItem} onClick={() => goToArticle(title, currentCatId)}>
                <span>{title}</span>
                <ArrowRight size={16} strokeWidth={1.33} className={styles.articleArrow} />
              </button>
            ))}
          </div>

          <div className={styles.stillNeedHelp}>
            <h3 className={styles.stillTitle}>Still need help?</h3>
            <p className={styles.stillText}>Our team is here to help. <button className={styles.reportLink} onClick={() => openReport(currentCat.id)}>Report an issue</button></p>
          </div>

          <PageFooter />
        </>)}

        {/* ── ARTICLE VIEW ── */}
        {view === 'article' && (<>
          <nav className={styles.breadcrumb}>
            <button className={styles.breadcrumbLink} onClick={goHome}>Help center</button>
            {currentCat && (<>
              <ChevronRight size={16} className={styles.breadcrumbSep} />
              <button className={styles.breadcrumbLink} onClick={() => goToCategory(currentCatId)}>{currentCat.label}</button>
            </>)}
            <ChevronRight size={16} className={styles.breadcrumbSep} />
            <span className={styles.breadcrumbCurrent}>{currentArticle}</span>
          </nav>

          <h1 className={styles.articleTitle}>{currentArticle}</h1>
          <div className={styles.articleDivider} />

          <div className={styles.articleBody}>
            {steps.map((s, i) => (
              <div key={i} className={styles.articleStep}>
                {/* The opening OVERVIEW section just restates the article
                    title, so its heading is dropped and the intro paragraph
                    leads straight off the divider. */}
                {!(i === 0 && s.title === 'OVERVIEW') && (
                  <p className={styles.articleStepTitle}>{s.title}</p>
                )}
                <p className={styles.articleStepBody}>{s.body}</p>
              </div>
            ))}
          </div>

          <div className={styles.stillNeedHelp}>
            <h3 className={styles.stillTitle}>Still need help?</h3>
            <p className={styles.stillText}>Our team is here to help. <button className={styles.reportLink} onClick={() => openReport(currentCatId)}>Report an issue</button></p>
          </div>

          <PageFooter />
        </>)}

        {/* ── VIDEO TUTORIAL VIEW ── */}
        {view === 'tutorial' && (<>
          <nav className={styles.breadcrumb}>
            <button className={styles.breadcrumbLink} onClick={goHome}>Help center</button>
            <ChevronRight size={16} className={styles.breadcrumbSep} />
            <button className={styles.breadcrumbLink} onClick={goToTutorials}>Video tutorials</button>
            {currentTutorial && (<>
              <ChevronRight size={16} className={styles.breadcrumbSep} />
              <span className={styles.breadcrumbCurrent}>{currentTutorial.title}</span>
            </>)}
          </nav>

          {!tutorialsLoaded ? (
            <p className={styles.tutorialMessage}>Loading tutorial…</p>
          ) : !currentTutorial ? (
            /* Unpublished since the link was shared, or never existed. */
            <p className={styles.tutorialMessage}>This tutorial isn&rsquo;t available.</p>
          ) : (<>
            <h1 className={styles.articleTitle}>{currentTutorial.title}</h1>
            <div className={styles.articleDivider} />

            <div className={styles.tutorialBody}>
              <RichTextView blocks={currentTutorial.description} />
              {!isBlankDocument(currentTutorial.description) && (
                <p className={`${styles.tutorialText} ${styles.tutorialLead}`}>
                  Prefer to watch? Here&rsquo;s the step-by-step:
                </p>
              )}
            </div>

            {videoEmbedUrl(currentTutorial.videoUrl) ? (
              <div className={styles.videoFrame}>
                <iframe
                  src={videoEmbedUrl(currentTutorial.videoUrl)}
                  title={currentTutorial.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : (
              <a className={styles.videoLink} href={currentTutorial.videoUrl} target="_blank" rel="noopener noreferrer">
                Watch the video
              </a>
            )}
          </>)}

          <div className={styles.stillNeedHelp}>
            <h3 className={styles.stillTitle}>Still need help?</h3>
            <p className={styles.stillText}>Our team is here to help. <button className={styles.reportLink} onClick={() => openReport(null)}>Report an issue</button></p>
          </div>

          <PageFooter />
        </>)}

        {/* ── REPORT MODAL ── */}
        {modal === 'report' && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Report an issue</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={18} /></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Category</label>
                  <select
                    className={`${styles.fieldSelect} ${reportErrors.category ? styles.inputError : ''}`}
                    value={reportForm.category}
                    onChange={e => { setReportForm(f => ({ ...f, category: e.target.value })); setReportErrors(er => ({ ...er, category: '' })); }}
                  >
                    <option value="">Select category</option>
                    {ISSUE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {reportErrors.category && <p className={styles.fieldErrorMsg}>{reportErrors.category}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Issue description</label>
                  <p className={styles.fieldHint}>Please describe the issue in detail.</p>
                  <div className={styles.textareaWrap}>
                    <textarea
                      className={`${styles.textarea} ${reportErrors.description ? styles.inputError : ''}`}
                      placeholder="Describe the issue you're facing…"
                      value={reportForm.description}
                      maxLength={1000}
                      rows={4}
                      onChange={e => { setReportForm(f => ({ ...f, description: e.target.value })); setReportErrors(er => ({ ...er, description: '' })); }}
                    />
                    <span className={styles.charCount}>{reportForm.description.length}/1000</span>
                  </div>
                  {reportErrors.description && <p className={styles.fieldErrorMsg}>{reportErrors.description}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel}>Attachment <span className={styles.optional}>(optional)</span></label>
                  <p className={styles.fieldHint}>Upload a screenshot to help us understand the issue faster.</p>
                  <input ref={reportFileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => setReportFile(e.target.files[0] || null)} />
                  <div
                    className={`${styles.uploadZone} ${draggingReport ? styles.uploadZoneDragging : ''}`}
                    onClick={() => reportFileRef.current.click()}
                    onDragOver={e => { e.preventDefault(); setDraggingReport(true); }}
                    onDragLeave={() => setDraggingReport(false)}
                    onDrop={e => handleDrop(e, setReportFile, setDraggingReport)}
                  >
                    <svg className={styles.uploadZoneBorder}>
                      <rect x="0" y="0" width="100%" height="100%" rx="10" ry="10" />
                    </svg>
                    {reportFile ? (
                      <><Paperclip size={22} className={styles.uploadIcon} /><span className={styles.uploadText}>{reportFile.name}</span></>
                    ) : (
                      <><Upload size={22} className={styles.uploadIcon} /><span className={styles.uploadText}>Upload or drag file</span></>
                    )}
                  </div>
                </div>
              </div>
              <div className={`${styles.modalFooter} ${styles.footerSpread}`}>
                <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
                <button className={styles.submitBtn} onClick={submitReport} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit issue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ADD VIDEO TUTORIAL MODAL (admins) ── */}
        {modal === 'tutorial' && isAdmin && (
          <div className={styles.overlay} onClick={closeModal}>
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-tutorial-title"
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2 id="add-tutorial-title" className={styles.modalTitle}>Add video tutorial</h2>
                <button className={styles.closeBtn} onClick={closeModal} aria-label="Close"><X size={18} /></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel} htmlFor="tutorial-title">Title</label>
                  <div className={styles.inputWrap}>
                    <input
                      id="tutorial-title"
                      type="text"
                      className={`${styles.fieldInput} ${styles.fieldInputCounted}`}
                      value={tutorialForm.title}
                      maxLength={TUTORIAL_TITLE_MAX}
                      onChange={e => setTutorialForm(f => ({ ...f, title: e.target.value }))}
                    />
                    <span className={styles.inputCount}>{tutorialForm.title.length}/{TUTORIAL_TITLE_MAX}</span>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <RichTextEditor
                    id="tutorial-description"
                    ariaLabelledBy="tutorial-description-label"
                    maxLength={TUTORIAL_DESCRIPTION_MAX}
                    placeholder="A short summary of what the video covers."
                    onChange={description => setTutorialForm(f => ({ ...f, description }))}
                    renderToolbar={toolbar => (
                      <div className={styles.labelRow}>
                        <span id="tutorial-description-label" className={styles.fieldLabel}>Description</span>
                        {toolbar}
                      </div>
                    )}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.fieldLabel} htmlFor="tutorial-video">Video link</label>
                  <p className={styles.fieldHint} id="tutorial-video-hint">Paste a YouTube or Loom URL.</p>
                  <input
                    id="tutorial-video"
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    className={`${styles.fieldInput} ${videoTouched && videoLinkInvalid ? styles.inputError : ''}`}
                    value={tutorialForm.videoUrl}
                    aria-describedby="tutorial-video-hint"
                    aria-invalid={videoTouched && videoLinkInvalid}
                    onChange={e => setTutorialForm(f => ({ ...f, videoUrl: e.target.value }))}
                    onBlur={() => setVideoTouched(true)}
                  />
                  {videoTouched && videoLinkInvalid && (
                    <p className={styles.fieldErrorMsg} role="alert">Enter a YouTube or Loom video link.</p>
                  )}
                </div>

                {tutorialError && <p className={styles.fieldErrorMsg} role="alert">{tutorialError}</p>}
              </div>
              <div className={`${styles.modalFooter} ${styles.footerSpread} ${styles.tutorialFooter}`}>
                <button className={styles.cancelBtn} onClick={closeModal}>Cancel</button>
                <button className={styles.submitBtn} onClick={submitTutorial} disabled={!tutorialComplete || addingTutorial}>
                  {addingTutorial ? 'Adding…' : 'Add video tutorial'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ISSUE REPORTED MODAL ── */}
        {modal === 'reported' && reportedTicket && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Issue reported</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={18} /></button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.successBanner}>
                  <CircleCheck size={18} className={styles.successIcon} />
                  <span>Your issue has been successfully reported. Keep an eye on the Help Center section for updates.</span>
                </div>
                <p className={styles.detailSectionLabel}>DETAILS</p>
                <div className={styles.detailRows}>
                  <DetailRow label="Ticket ID"   value={`#${reportedTicket.id}`} />
                  <DetailRow label="Submitted"   value={reportedTicket.submittedFull} />
                  <DetailRow label="Status"      value={<StatusBadge status={reportedTicket.status} />} />
                  <DetailRow label="Category"    value={reportedTicket.category} />
                  <DetailRow label="Description" value={reportedTicket.description} />
                  {reportedTicket.attachment && <DetailRow label="Attachment" value={<>{reportedTicket.attachment} <Paperclip size={13} className={styles.attachIcon} /></>} />}
                </div>
              </div>
              <div className={`${styles.modalFooter} ${styles.footerSpread}`}>
                <button className={styles.cancelBtn} onClick={() => { setReportForm(EMPTY_REPORT); setReportErrors({}); setReportFile(null); setModal('report'); }}>Report another issue</button>
                <button className={styles.doneBtn} onClick={closeModal}>Done</button>
              </div>
            </div>
          </div>
        )}

        {/* ── TICKET DETAILS MODAL ── */}
        {modal === 'details' && selectedTicket && (
          <div className={styles.overlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Ticket details</h2>
                <button className={styles.closeBtn} onClick={closeModal}><X size={18} /></button>
              </div>
              <div className={styles.modalBody}>
                <p className={styles.detailSectionLabel}>DETAILS</p>
                <div className={styles.detailRows}>
                  <DetailRow label="Ticket ID" value={`#${selectedTicket.id}`} />
                  <DetailRow label="Submitted" value={selectedTicket.submittedFull || selectedTicket.submitted} />
                  <DetailRow label="Status"    value={<StatusBadge status={selectedTicket.status} />} />
                  <DetailRow label="Category"  value={selectedTicket.category} />

                  {loadingMessages ? (
                    <DetailRow label="Description" value="Loading…" />
                  ) : ticketMessages.length === 0 ? (
                    <DetailRow label="Description" value={selectedTicket.issue} />
                  ) : ticketMessages.length === 1 ? (
                    <DetailRow label="Description" value={ticketMessages[0].message} />
                  ) : (
                    <DetailTimelineRow label="Description" entries={ticketMessages.map((msg, i) => ({
                      /* A support reply is mirrored out of the ticket's Slack
                         thread, so it still carries Slack's own *bold* markers.
                         The member's own updates are typed into the box below
                         and stay exactly as written. */
                      content:  msg.fromSupport
                        ? <SlackText as="span" text={msg.message} className={styles.slackMessage} />
                        : msg.message,
                      sublabel: msg.fromSupport
                        ? 'Customer support'
                        : (i === 0 ? 'Original message' : 'Additional detail'),
                    }))} />
                  )}

                  {!loadingMessages && (() => {
                    const withAttach = ticketMessages.filter(m => m.attachmentUrl);
                    if (!withAttach.length) return null;
                    if (withAttach.length === 1) return (
                      <DetailRow label="Attachment" value={
                        <a href={withAttach[0].attachmentUrl} target="_blank" rel="noreferrer" className={styles.attachLink}>
                          {withAttach[0].attachmentName || 'View attachment'} <Paperclip size={13} className={styles.attachIcon} />
                        </a>
                      } />
                    );
                    return (
                      <DetailTimelineRow label="Attachment" entries={withAttach.map((msg, i) => ({
                        content:  <a href={msg.attachmentUrl} target="_blank" rel="noreferrer" className={styles.attachLink}>{msg.attachmentName || 'View attachment'} <Paperclip size={13} className={styles.attachIcon} /></a>,
                        sublabel: i === 0 ? 'Original attachment' : 'Additional attachment',
                      }))} />
                    );
                  })()}
                </div>

                {canAddUpdate && (<>
                  <div className={styles.detailDivider} />
                  <p className={styles.updateLabel}>Need to add information?</p>
                  <div className={styles.textareaWrap}>
                    <textarea
                      className={styles.textarea}
                      placeholder="Add additional details…"
                      value={updateText}
                      maxLength={1000}
                      rows={4}
                      onChange={e => setUpdateText(e.target.value)}
                    />
                    <span className={styles.charCount}>{updateText.length}/1000</span>
                  </div>
                </>)}
              </div>
              {canAddUpdate && (<>
                <input ref={updateFileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => setUpdateFile(e.target.files[0] || null)} />
                <div className={`${styles.modalFooter} ${styles.footerSpread}`}>
                  <button className={styles.uploadBtn} onClick={() => updateFileRef.current.click()}>
                    <Paperclip size={15} /> {updateFile ? updateFile.name : 'Upload screenshot'}
                  </button>
                  <button
                    className={styles.submitBtn}
                    onClick={addUpdate}
                    disabled={addingUpdate || (!updateText.trim() && !updateFile)}
                  >{addingUpdate ? 'Sending…' : 'Add update'}</button>
                </div>
              </>)}
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span className={styles.detailValue}>{value}</span>
    </div>
  );
}

function DetailTimelineRow({ label, entries }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <div className={styles.timelineEntries}>
        {entries.map((entry, i) => (
          <div key={i} className={styles.timelineEntry}>
            <div className={styles.timelineEntryMain}>
              <span className={styles.timelineEntryText}>{entry.content}</span>
              <span className={styles.timelineEntrySubLabel}>{entry.sublabel}</span>
            </div>
            <div className={styles.timelineDotCol}>
              <span className={`${styles.timelineDot} ${i === 0 ? styles.timelineDotGreen : styles.timelineDotGrey}`} />
              {i < entries.length - 1 && <span className={styles.timelineLine} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default HelpCenter;
